import React, { useEffect, useState, useCallback } from "react";
import { CalendarCheck, AlertCircle } from "lucide-react";
import CalendarImport from "@/components/CalendarImport";
import api from "@/lib/api";

function relTime(iso) {
  if (!iso) return "not yet";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "recently";
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric" });
}

export default function ImportHealthBadge({ onChange }) {
  const [imports, setImports] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/calendar-imports"); setImports(data.items || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange && onChange(); };

  if (imports.length === 0) return null;

  const errored = imports.filter((i) => i.last_status === "error");
  const lastSynced = imports
    .map((i) => i.last_synced)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];

  const hasError = errored.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="import-health-badge"
        title={hasError ? (errored[0].last_error || "A calendar failed to sync") : `Last synced ${relTime(lastSynced)}`}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
          hasError
            ? "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
            : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        }`}
      >
        {hasError ? <AlertCircle className="h-3.5 w-3.5" /> : <CalendarCheck className="h-3.5 w-3.5" />}
        {hasError
          ? `Calendar sync error${errored.length > 1 ? ` (${errored.length})` : ""}`
          : `Calendars synced ${relTime(lastSynced)}`}
      </button>
      <CalendarImport open={open} onOpenChange={setOpen} onChange={refresh} hideTrigger />
    </>
  );
}
